import '@xyflow/react/dist/base.css';
import { useCallback, useContext, useMemo } from 'react';
import { ReactFlow, NodeTypes, ReactFlowInstance, SelectionMode } from '@xyflow/react';
import { FloatingEdge, RFEdgeFloatingEdge } from './edges/FloatingEdge';
import { EditableNode, RegularStep, DropZoneNode } from './nodes';
import { CanvasBackground } from './canvas-background/CanvasBackground';
import { CanvasControls } from './canvas-controls/CanvasControls';
import {
  CanvasElementType,
  SweepNodesChangeEvent,
  CanvasMode,
  DEFAULT_PREVIEW_MIN_ZOOM,
  DEFAULT_CANVAS_MIN_ZOOM,
} from './canvas-types';
import { GroupNode } from './nodes/GroupNode';
import { Box } from '@mui/material';
import GroupLabelNode from './nodes/GroupLabelNode';
import {
  getFitViewOptions,
  useCenterOnFirstNodeEffect,
} from './effects/useCenterOnFirstNodeEffect';
import { useMoveGroups } from './useMoveGroups';
import {
  CanvasWrapperResizeObserverContext,
  CanvasWrapperResizeObserverProvider,
} from './internal-context/CanvasWrapperDimensionsContext';
import { PluginTypes } from '../../types/enums/PluginTypes';
import { GroupOverlay } from './nodes/GroupOverlay';
import React from 'react';
import { useSweepCanvasState } from './internal-context/CanvasStateContext';
import { useCanvasZoomStyles } from './useCanvasZoomStyles';
import { useOnNodeChange } from './node-changes-event-handlers/useOnNodeChange';
import { GhostNode } from './nodes/GhostNode';
import {
  SweepCanvasProps,
  SweepCanvasPropsCtxProvider,
  useSweepCanvasPropsCtx,
} from './internal-context/SweepCanvasPropsCtx';
import { SweepCanvasRfNode } from './canvas-types/nodeTypesData';
import { useHighlightNodesAndEdges } from './highlight/useHighlightNodesAndEdges';
import { useContextZoomEffect } from './effects/useContextZoomEffect';
import { useNewNodeOnEmptyGroupEffect } from './effects/useNewNodeOnEmptyGroupEffect';
import { useFitEditingNodeToViewEffect } from './effects/useFitElementsToViewEffect';
import { useLoadNodesAndEdges } from './useLoadNodesAndEdges';
import { useFitViewIfPreviewOnResizeEffect } from './effects/useFitViewIfPreviewOnResizeEffect';
import {
  CanvasControlsCtxProvider,
  SweepCanvasSelectionMode,
  useCanvasControlsCtx,
} from './canvas-controls/CanvasControlsCtx';
import { useMultipleSelection } from './canvas-controls/useMultipleSelection';
import { ReorderStepsCtxProvider, useReorderStepsCtx } from './reorder-steps-mode/ReorderStepsCtx';
import { ReorderStepsControls } from './reorder-steps-mode/ReorderStepsControls';
import { ReorderNode } from './nodes/reorder-node/ReorderNode';
import { useOnReorderStepsNodesChange } from './reorder-steps-mode/onReorderStepsNodesChange';
import { SweepCanvasProvider, SweepCanvasProvidersInitializedCtx } from './SweepCanvasProvider';
import { useReorderStepsEffect } from './reorder-steps-mode/useReorderStepsEffect';
import { TemporaryTransformationsCtxProvider } from './factories/TemporaryTransformationsCtx';
import { HighlightEntityProvider } from './highlight/HighlightEntityContext';
import { useCalculateInitialViewport } from './useCalculateInitialViewport';

const edgeTypes = {
  floating: FloatingEdge,
};

export type OnSweepNodesChange = (changes: SweepNodesChangeEvent[]) => any;

export type OnPluginClickEvent = (props: {
  parentId: string;
  pluginId: PluginTypes;
  objectType: string;
  event: React.MouseEvent;
}) => void;

const proOptions = {
  account: 'paid-pro',
  hideAttribution: true,
};

const nodeTypes: NodeTypes = {
  [CanvasElementType.REGULAR]: RegularStep,
  [CanvasElementType.EDITABLE]: EditableNode,
  [CanvasElementType.DROP_ZONE_NODE]: DropZoneNode,
  [CanvasElementType.GROUP]: GroupNode,
  [CanvasElementType.GROUP_LABEL]: GroupLabelNode,
  [CanvasElementType.GROUP_OVERLAY]: GroupOverlay,
  [CanvasElementType.GHOST_NODE]: GhostNode,
  [CanvasElementType.REORDER_NODE]: ReorderNode,
};

const PreviewOverlay = () => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0)',
      zIndex: 12,
    }}
  />
);

const disableOnlyRenderVisibleElements =
  new URLSearchParams(window.location.search).get('disableOnlyRenderVisibleElements') === 'true';

const SweepCanvas = () => {
  const {
    moveGroups,
    showControls,
    isLoadingCursor,
    canvasMode = CanvasMode.DEFAULT,
    disableMultiSelection,
  } = useSweepCanvasPropsCtx();

  const { setReactFlowInstance } = useSweepCanvasState();

  const isInGroupMouseMoveMode = Boolean(moveGroups);

  const preview = canvasMode !== CanvasMode.DEFAULT;

  const { canvasWrapperRef } = useContext(CanvasWrapperResizeObserverContext);
  const { isInReorderStepsMode } = useReorderStepsCtx();

  const { startHighlightNodeAndConnectedEdges, startHighlightEdge } =
    useHighlightNodesAndEdges(isInReorderStepsMode);

  const { handleOnGroupsDropClick, onMoveGroupsMouseMoveHandler: onMoveGroupsMouseMove } =
    useMoveGroups();

  const { nodes, edges, regularNodes } = useLoadNodesAndEdges({
    disableExternalChanges: isInReorderStepsMode,
  });

  const showZoomControls =
    showControls ?? [CanvasMode.DEFAULT, CanvasMode.PREVIEW2].includes(canvasMode);

  useFitViewIfPreviewOnResizeEffect();

  const { onNodesChange, onEdgesChange } = useOnNodeChange();
  const { onReorderStepsNodesChange } = useOnReorderStepsNodesChange();
  const _onNodesChange = isInReorderStepsMode ? onReorderStepsNodesChange : onNodesChange;

  useContextZoomEffect();
  useNewNodeOnEmptyGroupEffect();
  useFitEditingNodeToViewEffect();
  useCenterOnFirstNodeEffect();

  const { sx, classes } = useCanvasZoomStyles();

  const fitViewOptions = useMemo(() => {
    return {
      nodes: regularNodes,
      ...getFitViewOptions(canvasMode),
    };
  }, [canvasMode, regularNodes]);

  const onInit = useCallback(
    (reactFlowInstance: ReactFlowInstance<SweepCanvasRfNode, RFEdgeFloatingEdge>) => {
      setReactFlowInstance(reactFlowInstance);
    },
    [setReactFlowInstance],
  );

  const onNodeMouseMove = useCallback(
    (event: any, node: SweepCanvasRfNode) => {
      if (node.type === CanvasElementType.GROUP_LABEL || node.type === CanvasElementType.REGULAR) {
        startHighlightNodeAndConnectedEdges(node.id);
      }
    },
    [startHighlightNodeAndConnectedEdges],
  );

  const onEdgeMouseMove = useCallback(
    (event: any, edge: RFEdgeFloatingEdge) => {
      startHighlightEdge(edge.id);
    },
    [startHighlightEdge],
  );

  // Enforce the order of the nodes to be groups first
  const sortedNodes = useMemo(
    () =>
      nodes.sort((a, b) => {
        if (a.type === CanvasElementType.GROUP && b.type !== CanvasElementType.GROUP) {
          return -1;
        }
        if (a.type !== CanvasElementType.GROUP && b.type === CanvasElementType.GROUP) {
          return 1;
        }
        return 0;
      }),
    [nodes],
  );

  const { onSelectionChange } = useMultipleSelection();
  const { selectionMode } = useCanvasControlsCtx();
  const minZoom = useMemo(() => {
    if (isInReorderStepsMode) {
      return 0.5;
    }
    return preview ? DEFAULT_PREVIEW_MIN_ZOOM : DEFAULT_CANVAS_MIN_ZOOM;
  }, [isInReorderStepsMode, preview]);

  useReorderStepsEffect();

  const initialViewport = useCalculateInitialViewport(sortedNodes, minZoom);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        backgroundColor: '#fff',
        '& .react-flow__pane': {
          cursor: isLoadingCursor ? 'wait' : undefined,
          '&.selection': {
            cursor: 'default',
          },
        },
        ...sx,
      }}
      ref={canvasWrapperRef}
      data-testid="sweep-canvas"
      className={classes.join(' ')}
      id="sweep-canvas"
    >
      <ReactFlow
        proOptions={proOptions}
        nodes={sortedNodes}
        edges={edges}
        edgeTypes={edgeTypes}
        nodeTypes={nodeTypes}
        onEdgesChange={onEdgesChange}
        onNodesChange={_onNodesChange}
        onNodeMouseMove={isInGroupMouseMoveMode ? undefined : onNodeMouseMove}
        onEdgeMouseMove={isInGroupMouseMoveMode ? undefined : onEdgeMouseMove}
        onMouseMove={onMoveGroupsMouseMove}
        onClick={handleOnGroupsDropClick}
        onInit={onInit}
        minZoom={minZoom}
        fitView
        fitViewOptions={fitViewOptions}
        defaultViewport={initialViewport}
        zoomOnScroll={!preview}
        panOnScroll
        onlyRenderVisibleElements={!disableOnlyRenderVisibleElements}
        onSelectionChange={onSelectionChange}
        selectionMode={SelectionMode.Partial}
        elementsSelectable={!isInReorderStepsMode}
        selectionOnDrag
        elevateNodesOnSelect={false}
        panOnDrag={selectionMode === SweepCanvasSelectionMode.panOnDrag}
        selectNodesOnDrag={selectionMode === SweepCanvasSelectionMode.selectOnDrag}
        deleteKeyCode={null}
      >
        <CanvasBackground />
        {showZoomControls ? (
          <CanvasControls disableMultiSelection={isInReorderStepsMode || disableMultiSelection} />
        ) : (
          <PreviewOverlay />
        )}
        <ReorderStepsControls />
      </ReactFlow>
    </Box>
  );
};

const SweepCanvasWithProviders = (props: SweepCanvasProps) => {
  const isInitialized = useContext(SweepCanvasProvidersInitializedCtx);

  const render = () => {
    return (
      <SweepCanvasPropsCtxProvider value={props}>
        <ReorderStepsCtxProvider>
          <CanvasControlsCtxProvider>
            <TemporaryTransformationsCtxProvider>
              <CanvasWrapperResizeObserverProvider>
                <HighlightEntityProvider>
                  <SweepCanvas />
                </HighlightEntityProvider>
              </CanvasWrapperResizeObserverProvider>
            </TemporaryTransformationsCtxProvider>
          </CanvasControlsCtxProvider>
        </ReorderStepsCtxProvider>
      </SweepCanvasPropsCtxProvider>
    );
  };

  if (!isInitialized) {
    return <SweepCanvasProvider>{render()}</SweepCanvasProvider>;
  }

  return render();
};

export { SweepCanvasWithProviders as SweepCanvas };
